/**
 * List-view URL state — the single owner of parse + serialize for every
 * list search param, shared by the master (`/`) and per-project
 * (`/board/$projectId`) surfaces.
 *
 * Two conventions worth knowing before touching this:
 *
 * 1. **The layout param is `layout`, NOT `view`.** TanStack Router unions
 *    same-named search keys across routes, and `view` is already claimed on
 *    both of these routes by board-filter semantics (`team-work | my-work |
 *    …`). Reusing it would collide.
 * 2. **List filter params carry an `l` prefix** (`lq`, `lkind`, `lstatus`,
 *    `lassignee`, `lproject`) so they can never be confused with the board's
 *    own `q` / `kinds` / `assignees`. The two filter sets are deliberately
 *    independent: the board's are client-side derivations, the list's are
 *    server query params, and a user who filtered the board to "my work"
 *    does not expect that to follow them into a lookup surface.
 *
 * Any filter or sort change resets `page` to 1 IN THE SAME navigation — as a
 * follow-up effect it would produce a visible double-fetch.
 */

import { useCallback, useMemo } from 'react'

import {
  CARD_LIST_SORT_KEYS,
  type CardListColumnName,
  type CardListSortKey,
  type CardListSortOrder,
  type DeliverableKind,
} from '@/api/types-bridge'

export type BoardLayout = 'board' | 'list'

export const DEFAULT_LAYOUT: BoardLayout = 'board'
export const DEFAULT_SORT: CardListSortKey = 'created'
export const DEFAULT_ORDER: CardListSortOrder = 'desc'
export const LIST_PAGE_SIZE = 50

/** The list half of a route's search params. */
export interface ListViewSearch {
  layout?: BoardLayout
  sort?: CardListSortKey
  dir?: CardListSortOrder
  page?: number
  lq?: string
  lkind?: readonly DeliverableKind[]
  lstatus?: readonly CardListColumnName[]
  lassignee?: readonly string[]
  lproject?: string
}

/* ------------------------------ Parsers ---------------------------------- */

const KIND_VALUES = new Set<DeliverableKind>(['task', 'ticket', 'event', 'announcement'])
const SORT_VALUES = new Set<string>(CARD_LIST_SORT_KEYS)
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

/**
 * Accepts a parsed array (TanStack's JSON deserializer) or a
 * comma-separated string (hand-typed / shared URLs), same as the board's
 * own param parsers.
 */
function parseStringList(raw: unknown): string[] | undefined {
  const candidates: string[] = []
  if (Array.isArray(raw)) {
    for (const v of raw) if (typeof v === 'string') candidates.push(v)
  } else if (typeof raw === 'string' && raw.length > 0) {
    candidates.push(...raw.split(','))
  } else {
    return undefined
  }
  const out = candidates.map((s) => s.trim()).filter((s) => s.length > 0)
  return out.length > 0 ? out : undefined
}

/**
 * Parse the list half of a route's search params. Unknown members are
 * dropped rather than rejected — an unparseable URL should degrade to a
 * usable list, not a crash. (The BE is the strict half: it 400s an unknown
 * filter value so a typo can't silently widen a result set.)
 */
export function parseListViewSearch(search: Record<string, unknown>): ListViewSearch {
  const kinds = parseStringList(search.lkind)
    ?.map((v) => v.toLowerCase())
    .filter((v): v is DeliverableKind => KIND_VALUES.has(v as DeliverableKind))

  // Status names are project-owned strings, so there is no closed set to
  // validate against. An unknown name is harmless — it simply matches no
  // rows — whereas dropping it would silently widen the result set.
  const columns: CardListColumnName[] | undefined = parseStringList(search.lstatus)

  // `lassignee` and `lproject` reach the BE, which 400s an unrecognised
  // value — and a 400 renders the whole list as an error panel. The docblock
  // above promises a malformed URL degrades to a usable list, so drop bad
  // members here rather than shipping them.
  const assignees = parseStringList(search.lassignee)?.filter(
    (v) => v === 'unassigned' || UUID_RE.test(v),
  )

  const rawPage = typeof search.page === 'number' ? search.page : Number(search.page)
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? Math.floor(rawPage) : undefined

  return {
    layout: search.layout === 'list' ? 'list' : undefined,
    sort:
      typeof search.sort === 'string' && SORT_VALUES.has(search.sort)
        ? (search.sort as CardListSortKey)
        : undefined,
    dir: search.dir === 'asc' || search.dir === 'desc' ? search.dir : undefined,
    page,
    lq: typeof search.lq === 'string' && search.lq.length > 0 ? search.lq : undefined,
    lkind: kinds && kinds.length > 0 ? kinds : undefined,
    lstatus: columns && columns.length > 0 ? columns : undefined,
    lassignee: assignees && assignees.length > 0 ? assignees : undefined,
    lproject:
      typeof search.lproject === 'string' && UUID_RE.test(search.lproject)
        ? search.lproject
        : undefined,
  }
}

/** `['task','ticket'] → 'task,ticket'`; empty / absent → `undefined`. */
const csvOrUndefined = (values: readonly string[] | undefined): string | undefined =>
  values && values.length > 0 ? values.join(',') : undefined

/* ------------------------------- Hook ------------------------------------ */

export interface ListFilters {
  q: string | undefined
  kinds: readonly DeliverableKind[] | undefined
  columns: readonly CardListColumnName[] | undefined
  assignees: readonly string[] | undefined
  /** Master surface only — the per-project surface pins its own project. */
  projectId: string | undefined
}

export interface ListFilterUpdates {
  q?: string | undefined
  kinds?: readonly DeliverableKind[] | undefined
  columns?: readonly CardListColumnName[] | undefined
  assignees?: readonly string[] | undefined
  projectId?: string | undefined
}

export interface UseListViewStateResult {
  layout: BoardLayout
  setLayout: (next: BoardLayout) => void
  sort: CardListSortKey
  order: CardListSortOrder
  /**
   * Apply a sort key. Clicking the already-active column toggles direction;
   * a new column starts at its own natural direction (ascending for text and
   * dates you read forward, descending for "most / newest first").
   */
  setSort: (key: CardListSortKey, defaultDir: CardListSortOrder) => void
  page: number
  setPage: (next: number) => void
  filters: ListFilters
  setFilters: (updates: ListFilterUpdates) => void
  clearFilters: () => void
  hasActiveFilters: boolean
}

interface UseListViewStateArgs {
  search: ListViewSearch
  /**
   * Route-bound navigation. Each surface supplies its own so this hook
   * stays route-agnostic (and so neither route's typed search shape leaks
   * into the other).
   */
  applySearch: (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => void
}

export function useListViewState({
  search,
  applySearch,
}: UseListViewStateArgs): UseListViewStateResult {
  const layout = search.layout ?? DEFAULT_LAYOUT
  const sort = search.sort ?? DEFAULT_SORT
  const order = search.dir ?? DEFAULT_ORDER
  const page = search.page ?? 1

  const filters: ListFilters = useMemo(
    () => ({
      q: search.lq,
      kinds: search.lkind,
      columns: search.lstatus,
      assignees: search.lassignee,
      projectId: search.lproject,
    }),
    [search.lq, search.lkind, search.lstatus, search.lassignee, search.lproject],
  )

  const hasActiveFilters =
    Boolean(filters.q) ||
    Boolean(filters.kinds?.length) ||
    Boolean(filters.columns?.length) ||
    Boolean(filters.assignees?.length) ||
    Boolean(filters.projectId)

  const setLayout = useCallback(
    (next: BoardLayout) => {
      applySearch((prev) => ({
        ...prev,
        // The default is stripped so a plain `/` or `/board/<id>` URL stays
        // clean, and so the board surface never carries list cruft.
        layout: next === DEFAULT_LAYOUT ? undefined : next,
      }))
    },
    [applySearch],
  )

  const setSort = useCallback(
    (key: CardListSortKey, defaultDir: CardListSortOrder) => {
      applySearch((prev) => {
        const prevSort = (prev.sort as CardListSortKey | undefined) ?? DEFAULT_SORT
        const prevDir = (prev.dir as CardListSortOrder | undefined) ?? DEFAULT_ORDER
        const nextDir: CardListSortOrder =
          prevSort === key ? (prevDir === 'asc' ? 'desc' : 'asc') : defaultDir
        return {
          ...prev,
          sort: key === DEFAULT_SORT && nextDir === DEFAULT_ORDER ? undefined : key,
          dir: key === DEFAULT_SORT && nextDir === DEFAULT_ORDER ? undefined : nextDir,
          // A re-sort invalidates the current offset.
          page: undefined,
        }
      })
    },
    [applySearch],
  )

  const setPage = useCallback(
    (next: number) => {
      applySearch((prev) => ({ ...prev, page: next <= 1 ? undefined : next }))
    },
    [applySearch],
  )

  const setFilters = useCallback(
    (updates: ListFilterUpdates) => {
      applySearch((prev) => {
        const next = { ...prev }
        if ('q' in updates) next.lq = updates.q && updates.q.length > 0 ? updates.q : undefined
        // Written as comma-separated strings rather than arrays: TanStack
        // serializes an array as JSON (`?lkind=%5B%22ticket%22%5D`), and a
        // link people paste into chat to say "here's the old ticket" should
        // stay readable. `parseListViewSearch` accepts both forms.
        if ('kinds' in updates) next.lkind = csvOrUndefined(updates.kinds)
        if ('columns' in updates) next.lstatus = csvOrUndefined(updates.columns)
        if ('assignees' in updates) next.lassignee = csvOrUndefined(updates.assignees)
        if ('projectId' in updates) next.lproject = updates.projectId || undefined
        // Any filter change invalidates the current offset. Same navigation,
        // not a follow-up effect — otherwise the user sees two fetches.
        next.page = undefined
        return next
      })
    },
    [applySearch],
  )

  const clearFilters = useCallback(() => {
    applySearch((prev) => ({
      ...prev,
      lq: undefined,
      lkind: undefined,
      lstatus: undefined,
      lassignee: undefined,
      lproject: undefined,
      page: undefined,
      // Sort is deliberately preserved — clearing filters narrows the set,
      // it doesn't reset how the user wants to read it.
    }))
  }, [applySearch])

  return {
    layout,
    setLayout,
    sort,
    order,
    setSort,
    page,
    setPage,
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
  }
}
